import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { makeTestId } from '../../utils/Declarative';
import { assignRef, PropsAndElementAttrs } from '../../utils/ReactHelpers';
import { ExpandoInstance, ExpandoWidget, ExpandoWidgetProps } from './ExpandoWidget';

export type AccordionProps = {
  /**
   * By default, the accordion allows multiple expandos in `children` to be expanded.
   * Set this prop to `true` to automatically collapse all other expandos when expanding a particular expando.
   */
  autoCollapse?: boolean;
  /**
   * The list of `ExpandoWidget` to display. The expandos may be used in controlled or uncontrolled mode.
   *
   * **NOTE**: if you create a custom wrapper component around `ExpandoWidget`, it must expose a ref of type `ExpandoInstance`.
   * Typically this would be done via `React.forwardRef<ExpandoInstance, P>(...)`.
   */
  children: Array<React.ReactElement<ExpandoWidgetProps>>;
  /** Set this prop to `true` to show dividers between the expandos. */
  showDividers?: boolean;
  /**
   * Test id to be attached to the root element `data-tb-test-id` attribute.
   * The suffix `'-AccordionWidget'` will be automatically added
   */
  testId?: string;
};

const AccordionStyleName = TypeStyle.style({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  $nest: {
    '&.showDividers > * + *::before': { // match all child expandos except the first
      backgroundColor: '#E6E6E6',
      content: `""`,
      height: 1,
      marginBottom: 8,
      width: '100%', // ensures the divider can still extend the full width when the expando root element doesn't have `align-items: stretch`
    },
  }
});

export type AccordionInstance = {
  /**
   * Toggles an Expando.
   * @param expandoIndex the index of the Expando to toggle
   * @param expanded optional parameter, ```true``` to open or ```false``` to close
   */
  toggleAt: (expandoIndex: number, expanded?: boolean) => void;

  /**
   * Toggles all the Expandos.
   * @param expanded optional parameter, ```true``` to open or ```false``` to close
   */
  toggleAll: (expanded?: boolean) => void;
}

/**
 * Accordion is a vertically stacked list of two or more `ExpandoWidget` which reveal or hide additional sections of content.
 */
export const AccordionWidget = React.forwardRef<AccordionInstance, PropsAndElementAttrs<AccordionProps>>((props, accordionRef) => {
  const { autoCollapse, children, showDividers, testId, ...htmlAttrs } = props;
  const expandoRefs = React.useRef<Array<ExpandoInstance | null>>([]);

  React.useEffect(() => {
    expandoRefs.current = expandoRefs.current.slice(0, children.length);
  }, [children]);

  React.useImperativeHandle(accordionRef, () => ({
    toggleAt: (expandoIndex, expanded) => expandoRefs.current[expandoIndex]?.toggle(expanded),
    toggleAll: (expanded) => expandoRefs.current.forEach(expando => expando?.toggle(expanded)),
  }));

  return <div
    data-tb-test-id={makeTestId(testId, 'AccordionWidget')}
    {...htmlAttrs}
    className={TypeStyle.classes(AccordionStyleName, { showDividers }, htmlAttrs.className)}
  >
    {children.map((expandoEl, index) => React.cloneElement<React.ComponentPropsWithRef<typeof ExpandoWidget>>(expandoEl, {
      ...(autoCollapse && {
        onExpand: () => {
          expandoRefs.current.forEach((expando, expandoIndex) => {
            if (expandoIndex !== index) {
              expando?.toggle(false);
            }
          });

          expandoEl.props.onExpand?.();
        }
      }),
      ref: (expandoRef) => {
        expandoRefs.current[index] = expandoRef;
        assignRef((expandoEl as React.RefAttributes<ExpandoInstance>).ref, expandoRef);
      }
    }))}
  </div>;
});
AccordionWidget.displayName = 'AccordionWidget';
